import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format number as currency */
export function formatCurrency(value: number, decimals = 2): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Format number as percentage */
export function formatPct(value: number | null, decimals = 2): string {
  if (value === null || value === undefined) return "—";
  return `${value >= 0 ? "+" : ""}${value.toFixed(decimals)}%`;
}

/** Format large numbers compactly */
export function formatCompact(value: number): string {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/** P&L color class */
export function plColor(value: number | null): string {
  if (value === null || value === 0) return "text-zinc-400";
  return value > 0 ? "text-profit" : "text-loss";
}

/** Signal color class */
export function signalColor(signal: string): string {
  switch (signal) {
    case "BUY": return "signal-buy";
    case "SELL": return "signal-sell";
    case "HOLD": return "signal-hold";
    case "WATCH": return "signal-watch";
    case "REVIEW": return "signal-review";
    default: return "text-zinc-400";
  }
}

/** Score color — maps 0-10 score to a color class */
export function scoreColor(score: number): string {
  if (score >= 7.5) return "text-emerald-400";
  if (score >= 6) return "text-amber-400";
  if (score >= 4) return "text-zinc-300";
  return "text-red-400";
}

/** Score color with background fill class */
export function scoreBgClass(score: number): string {
  if (score >= 7.5) return "bg-[rgba(0,220,130,0.08)]";
  if (score >= 6) return "bg-amber-500/[0.06]";
  if (score >= 4) return "";
  return "bg-[rgba(255,87,87,0.06)]";
}

/** Relative time (e.g., "2h ago", "3d ago") */
export function timeAgo(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
